import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatViews, getCategoryName, type Video } from '@/utils/video';

interface HeroCarouselProps {
  videos: Video[];
}

export default function HeroCarousel({ videos }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      setActive((index + videos.length) % videos.length);
    },
    [videos.length],
  );

  useEffect(() => {
    if (paused || videos.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % videos.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [paused, videos.length]);

  if (videos.length === 0) return null;

  return (
    <section
      className="relative w-full h-[420px] md:h-[520px] lg:h-[560px] overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {videos.map((video, index) => (
        <div
          key={video.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={video.cover}
            alt={video.title}
            title={`${video.title} - 宅男视频`}
            className="w-full h-full object-top object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/30"></div>
        </div>
      ))}

      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-semibold mb-4">
              <i className="ri-fire-fill"></i>
              本周焦点推荐
            </span>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight line-clamp-3">
              {videos[active].title}
            </h1>
            <p className="mt-4 text-sm md:text-base text-primary-100 line-clamp-2 max-w-xl">
              {videos[active].description}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-primary-200">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary-800/80">
                {getCategoryName(videos[active].category)}
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-play-circle-line"></i>
                {formatViews(videos[active].views)} 次播放
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-time-line"></i>
                {videos[active].duration}
              </span>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${videos[active].id}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-primary-950 font-semibold text-sm whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
              >
                <i className="ri-play-fill text-lg"></i>
                立即观看
              </Link>
              <Link
                to={`/category/${videos[active].category}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-primary-300 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-800 transition-colors cursor-pointer"
              >
                浏览同分类
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一个"
        onClick={() => goTo(active - 1)}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 items-center justify-center w-10 h-10 rounded-full bg-primary-950/50 text-background-50 hover:bg-accent-500 hover:text-primary-950 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一个"
        onClick={() => goTo(active + 1)}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 items-center justify-center w-10 h-10 rounded-full bg-primary-950/50 text-background-50 hover:bg-accent-500 hover:text-primary-950 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {videos.map((video, index) => (
          <button
            key={video.id}
            type="button"
            aria-label={`切换到第 ${index + 1} 张`}
            onClick={() => goTo(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === active ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50'
            }`}
          />
        ))}
      </div>
    </section>
  );
}